export type BootstrapMetricName =
  | 'clerkBootstrap'
  | 'userMe'
  | 'leagueLoad'
  | 'authBootstrap'
  | 'dashboardReady';

export interface BootstrapMetricRecord {
  name: BootstrapMetricName;
  durationMs: number;
  recordedAtIso: string;
  detail?: Record<string, string | number | boolean | null>;
}

export interface BootstrapPerformanceSnapshot {
  sessionStartedAtIso: string;
  pathname: string;
  metrics: BootstrapMetricRecord[];
}

interface BootstrapPerformanceState {
  pathname: string;
  sessionStartedAtIso: string;
  sessionStartedAtMs: number;
  metrics: BootstrapMetricRecord[];
}

declare global {
  interface Window {
    __ELM_PERF__?: {
      snapshot: () => BootstrapPerformanceSnapshot;
      reset: () => BootstrapPerformanceSnapshot;
    };
  }
}

const state: BootstrapPerformanceState = {
  pathname: '/',
  sessionStartedAtIso: new Date(0).toISOString(),
  sessionStartedAtMs: 0,
  metrics: [],
};

function nowIso(): string {
  return new Date().toISOString();
}

function currentPathname(): string {
  if (typeof window === 'undefined') {
    return state.pathname;
  }
  return window.location.pathname || '/';
}

function debugEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const search = window.location.search || '';
  const params = new URLSearchParams(search);
  if (params.get('debugPerf') === '1') {
    return true;
  }

  try {
    return window.localStorage.getItem('elm.debugPerf') === '1';
  } catch {
    return false;
  }
}

function emitDebugLog(metric: BootstrapMetricRecord): void {
  if (!debugEnabled() || typeof console === 'undefined') {
    return;
  }

  console.info('[ELM bootstrap perf]', {
    pathname: state.pathname,
    metric,
  });
}

function installDebugHandle(): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.__ELM_PERF__ = {
    snapshot: () => snapshotBootstrapPerformance(),
    reset: () => resetBootstrapPerformance(currentPathname()),
  };
}

export function snapshotBootstrapPerformance(): BootstrapPerformanceSnapshot {
  return {
    sessionStartedAtIso: state.sessionStartedAtIso,
    pathname: state.pathname,
    metrics: [...state.metrics],
  };
}

export function resetBootstrapPerformance(pathname: string = currentPathname()): BootstrapPerformanceSnapshot {
  state.pathname = pathname;
  state.sessionStartedAtIso = nowIso();
  state.sessionStartedAtMs = performance.now();
  state.metrics = [];
  installDebugHandle();
  return snapshotBootstrapPerformance();
}

export function recordBootstrapMetric(
  name: BootstrapMetricName,
  durationMs: number,
  detail?: Record<string, string | number | boolean | null>,
): BootstrapMetricRecord {
  if (!state.sessionStartedAtMs) {
    resetBootstrapPerformance(currentPathname());
  }

  const metric: BootstrapMetricRecord = {
    name,
    durationMs: Number(durationMs.toFixed(1)),
    recordedAtIso: nowIso(),
    ...(detail ? { detail } : {}),
  };

  state.metrics = [...state.metrics, metric];
  installDebugHandle();
  emitDebugLog(metric);
  return metric;
}

export function bootstrapElapsedMs(): number | null {
  if (!state.sessionStartedAtMs) {
    return null;
  }

  return Number((performance.now() - state.sessionStartedAtMs).toFixed(1));
}

export function resetBootstrapPerformanceForTests(): void {
  state.pathname = '/';
  state.sessionStartedAtIso = new Date(0).toISOString();
  state.sessionStartedAtMs = 0;
  state.metrics = [];

  if (typeof window !== 'undefined') {
    delete window.__ELM_PERF__;
  }
}
